import { useMemo } from 'react'
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs'
import { isRoleId, isTrue } from '@terros/common'
import { usePermissionCheck, useProfile, useRoles } from '@front/model'
import { useSelectedEntityQueries } from './useSelectedEntityQueries'
import {
  type CompareEntity,
  type CompareEntityError,
  type CompareEntityId,
  type CompareRoleOption,
  normalizeCompareEntityIds,
  toCompareEntity,
} from './compareUtil'

type RoleAccess = {
  isLoading: boolean
  error: CompareEntityError
  isDenied: boolean
}

type CompareEntitySelection = {
  entityIds: CompareEntityId[]
  selectedEntities: CompareEntity[]
  setEntityIds: (entityIds: CompareEntityId[]) => void
  roleOptions: CompareRoleOption[]
  isLoadingRoleOptions: boolean
  isLoadingEntities: boolean
  entitiesError: CompareEntityError
  roleAccess: RoleAccess
}

export const useCompareEntitySelection = (): CompareEntitySelection => {
  const [queryEntityIds, setQueryEntityIds] = useQueryState('entities', parseAsArrayOf(parseAsString).withDefault([]))
  const entityIds = normalizeCompareEntityIds(queryEntityIds)
  const selectedEntityQueries = useSelectedEntityQueries(entityIds)
  const { company } = useProfile()
  const {
    data: canManageRoles,
    isLoading: isLoadingRolePermission,
    error: rolePermissionError,
  } = usePermissionCheck({
    permissionType: 'role:manage',
    targetId: company?.companyId,
  })
  const { data: roles, isLoading: isLoadingRoles, error: rolesError } = useRoles({ enabled: isTrue(canManageRoles) })

  const selectedEntities = useMemo(
    () =>
      entityIds.map((entityId, index): CompareEntity => {
        const role = roles?.find((candidate) => candidate.roleId === entityId)
        return selectedEntityQueries[index]?.data ?? toCompareEntity({ id: entityId, name: role?.name ?? entityId })
      }),
    [entityIds, roles, selectedEntityQueries]
  )

  const hasSelectedRole = entityIds.some(isRoleId)
  const roleOptionsError = canManageRoles ? rolesError : undefined

  return {
    entityIds,
    selectedEntities,
    setEntityIds: setQueryEntityIds,
    roleOptions: canManageRoles ? (roles ?? []) : [],
    isLoadingRoleOptions: isTrue(canManageRoles) && isLoadingRoles,
    isLoadingEntities: isLoadingRolePermission || selectedEntityQueries.some((query) => query.isLoading),
    entitiesError: roleOptionsError ?? selectedEntityQueries.find((query) => query.error)?.error ?? undefined,
    roleAccess: {
      isLoading: hasSelectedRole && (isLoadingRolePermission || (isTrue(canManageRoles) && isLoadingRoles)),
      error: hasSelectedRole ? (rolePermissionError ?? rolesError) : undefined,
      isDenied: hasSelectedRole && !isLoadingRolePermission && !canManageRoles,
    },
  }
}
